/**
 * @fileoverview Per-page margin override popover, shared by the day-one
 * document preview and the settings-docs template preview.
 *
 * Opens from a "Page N margins" button on the page card. Blank inputs
 * inherit (placeholder shows the inherited value in the active unit);
 * typing a value saves an override for that page only; "Reset page"
 * clears it.
 */

import { useEffect, useMemo, useState } from "react";
import { Maximize2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays";
import { cn } from "@/utils/misc";
import {
  formatForUnit,
  parseInForUnit,
  useMarginUnit,
  type MarginUnit,
} from "@/utils/branding/marginUnits";
import type {
  DocumentPadding,
  PaddingSource,
} from "@/utils/branding/resolve-letterhead";
import { MarginsRulerDiagram, type DiagramValues } from "./MarginsRulerDiagram";
import {
  ZONES,
  ALL_FIELDS,
  toPatch,
  pageHasCustomMargins,
  type FieldKey,
  type PageMarginPatch,
} from "./PageMarginsPopover.helpers";
import { PAGE_FIT_LOCK_NOTE, type ZoneLocks } from "@/utils/branding/pageFit";

export interface PageMarginsPopoverProps {
  pageIndex: number;
  /** What the page gets when it sets nothing (variant layout or uniform). */
  inherited: DocumentPadding;
  /** The stored override for this page, if any. */
  current: PaddingSource | null;
  onSave: (padding: PageMarginPatch | null) => Promise<unknown> | undefined;
  canEdit: boolean;
  /** Zones whose artwork is page-fit on this page — their margins lock. */
  lockedZones?: Partial<ZoneLocks>;
  className?: string;
}

function seedDrafts(
  current: PaddingSource | null,
  unit: MarginUnit,
): Record<FieldKey, string> {
  const out: Partial<Record<FieldKey, string>> = {};
  for (const f of ALL_FIELDS) {
    const v = current?.[f.key];
    out[f.key] = v == null ? "" : formatForUnit(v, unit);
  }
  return out as Record<FieldKey, string>;
}

export function PageMarginsPopover({
  pageIndex,
  inherited,
  current,
  onSave,
  canEdit,
  lockedZones,
  className,
}: PageMarginsPopoverProps) {
  const [unit, setUnit] = useMarginUnit();
  const [open, setOpen] = useState(false);
  const [drafts, setDrafts] = useState(() => seedDrafts(current, unit));
  useEffect(() => {
    setDrafts(seedDrafts(current, unit));
  }, [current, unit]);
  const customised = pageHasCustomMargins(current);
  // Diagram: the page's effective margins (override over inherited), with
  // live values while a handle is being dragged.
  const [dragValues, setDragValues] = useState<DiagramValues | null>(null);
  const effective = useMemo(() => {
    const out: Partial<DiagramValues> = {};
    for (const f of ALL_FIELDS)
      out[f.key] = current?.[f.key] ?? inherited[f.resolved];
    return out as DiagramValues;
  }, [current, inherited]);

  const save = async (next: PaddingSource | null) => {
    try {
      await onSave(toPatch(next));
    } catch {
      toast.error("Failed to save page margins.");
    }
  };

  const commit = (key: FieldKey) => {
    const result = parseInForUnit(drafts[key] ?? "", unit);
    if (result.kind === "empty") {
      if (current?.[key] == null) return;
      const next = { ...current };
      delete next[key];
      void save(next);
      return;
    }
    if (result.kind !== "ok") {
      toast.error(result.reason);
      const currentValue = current?.[key];
      setDrafts((d) => ({
        ...d,
        [key]: currentValue != null ? formatForUnit(currentValue, unit) : "",
      }));
      return;
    }
    if (current?.[key] === result.px) return;
    void save({ ...(current ?? {}), [key]: result.px });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`page-margins-trigger-${pageIndex}`}
          className={cn(
            "flex items-center gap-1 rounded border px-2 py-1 text-[11px] font-medium shadow-sm transition",
            customised
              ? "border-primary/30 bg-primary/10 text-primary"
              : "border-border/60 bg-white text-primary/60 hover:text-primary",
            className,
          )}
          title={`Page ${pageIndex} margins`}
        >
          <Maximize2 className="size-3" />
          {customised
            ? `Page ${pageIndex}: custom margins`
            : `Page ${pageIndex} margins`}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[560px] p-3"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="mb-2 flex items-center justify-between gap-2">
          <span className="text-xs font-medium text-primary/70">
            Page {pageIndex} margins
          </span>
          <div
            role="radiogroup"
            className="inline-flex overflow-hidden rounded border border-border/60 text-[10px]"
          >
            {(["cm", "px"] as const).map((u) => (
              <button
                key={u}
                type="button"
                role="radio"
                aria-checked={unit === u}
                onClick={() => setUnit(u)}
                className={cn(
                  "px-1.5 py-0.5",
                  unit === u
                    ? "bg-primary/10 font-medium text-primary"
                    : "text-primary/45",
                )}
              >
                {u}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-[1fr_auto] gap-4">
          <div className="flex flex-col gap-2">
            {ZONES.map((zone) => {
              const locked =
                (zone.zone === "Header" && !!lockedZones?.header) ||
                (zone.zone === "Footer" && !!lockedZones?.footer);
              return (
                <div
                  key={zone.zone}
                  title={locked ? PAGE_FIT_LOCK_NOTE : undefined}
                >
                  <span className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-primary/35">
                    {zone.zone}
                    {locked ? " · set by letterhead" : ""}
                  </span>
                  <div className="flex flex-col gap-1">
                    {zone.fields.map((f) => (
                      <div
                        key={f.key}
                        className="flex items-center justify-between gap-2"
                      >
                        <label className="text-[11px] text-primary/50">
                          {f.label}
                        </label>
                        <div className="flex items-center gap-1">
                          <input
                            type="text"
                            inputMode="decimal"
                            aria-label={`Page ${pageIndex} ${zone.zone} ${f.label}`}
                            value={drafts[f.key] ?? ""}
                            placeholder={formatForUnit(
                              inherited[f.resolved],
                              unit,
                            )}
                            readOnly={!canEdit}
                            disabled={locked}
                            onChange={(e) =>
                              setDrafts((d) => ({
                                ...d,
                                [f.key]: e.target.value,
                              }))
                            }
                            onBlur={() => canEdit && !locked && commit(f.key)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                (e.target as HTMLInputElement).blur();
                              }
                            }}
                            className="h-6 w-16 rounded border border-border/50 bg-background px-1.5 text-center text-[11px] tabular-nums focus:border-primary/40 focus:outline-none disabled:cursor-not-allowed disabled:opacity-40"
                          />
                          <span className="text-[10px] text-primary/30">
                            {unit}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <MarginsRulerDiagram
            values={dragValues ?? effective}
            unit={unit}
            width={220}
            disabled={!canEdit}
            lockedZones={lockedZones}
            onChange={(key, px) =>
              setDragValues((prev) => ({ ...(prev ?? effective), [key]: px }))
            }
            onCommit={(key, px) => {
              setDragValues(null);
              void save({ ...(current ?? {}), [key]: px });
            }}
          />
        </div>
        <p className="mt-2 text-[10px] text-primary/35">
          Blank fields inherit. Page numbers follow the paginated output: if
          content moves, the override stays on this page number.
        </p>
        {customised && canEdit && (
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={() => void save(null)}
              className="flex items-center gap-1 rounded px-2 py-1 text-[11px] text-primary/50 hover:bg-secondary"
            >
              <RotateCcw className="size-3" /> Reset page
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
